import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';

export interface VirtualScrollerPageInfo {
  endIndex: number;
  endIndexWithBuffer: number;
  maxScrollPosition: number;
  scrollEndPosition: number;
  scrollStartPosition: number;
  startIndex: number;
  startIndexWithBuffer: number;
}

interface Viewport extends VirtualScrollerPageInfo {
  padding: number;
  scrollLength: number;
}

interface Dimensions {
  childHeight: number;
  childWidth: number;
  itemCount: number;
  itemsPerPage: number;
  itemsPerWrapGroup: number;
  maxScrollPosition: number;
  pageCountFractional: number;
  scrollLength: number;
  viewportLength: number;
  wrapGroupsPerPage: number;
}

interface WrapGroupDimension {
  childHeight: number;
  childWidth: number;
  items: unknown[];
}

/**
 * This code was pulled in (via co-pilot) from the previously used ngx-virtual-scroll library.
 * The advantage said library had over virtual scroll utilities provided by angular/cdk is that has better support for dynamic elements.
 *
 * Some modifications were also made to make the component standalone as well as remove the dependency on another external library, tween, in
 * favor of using requestAnimationFrame directly.
 *
 * The orignal code can be seen here:
 * https://github.com/rintoj/ngx-virtual-scroller/blob/d1aed1eae324643d51f042acb8d902d3d51aa999/src/virtual-scroller.ts
 *
 * Note that the source library is not compatible with modern versions of angular, so it can't be used directly.
 */
@Component({
  selector: 'app-virtual-scroller, virtual-scroller, [virtualScroller]',
  exportAs: 'virtualScroller',
  standalone: true,
  templateUrl: './virtual-scroller.component.html',
  styleUrl: './virtual-scroller.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.horizontal]': 'horizontal',
    '[class.vertical]': '!horizontal',
    '[class.self-scroll]': '!parentScroll',
    '[class.selfScroll]': '!parentScroll',
    '[class.rtl]': 'RTL',
  },
})
export class VirtualScrollerComponent implements AfterViewInit, AfterContentChecked, OnDestroy {
  @Input() executeRefreshOutsideAngularZone = false;
  @Input() checkResizeInterval = 1000;
  @Input() RTL = false;
  @Input() useMarginInsteadOfTranslate = false;
  @Input() modifyOverflowStyleOfParentScroll = true;
  @Input() stripedTable = false;
  @Input() scrollbarWidth?: number;
  @Input() scrollbarHeight?: number;
  @Input() childWidth?: number;
  @Input() childHeight?: number;
  @Input() ssrChildWidth?: number;
  @Input() ssrChildHeight?: number;
  @Input() ssrViewportWidth = 1920;
  @Input() ssrViewportHeight = 1080;
  @Input() scrollAnimationTime = 750;

  @Input()
  set scrollThrottlingTime(value: number | undefined) {
    this._scrollThrottlingTime = value ?? 0;
    this.attachScrollHandlers();
  }
  get scrollThrottlingTime(): number {
    return this._scrollThrottlingTime;
  }

  @Input()
  set scrollDebounceTime(value: number | undefined) {
    this._scrollDebounceTime = value ?? 0;
    this.attachScrollHandlers();
  }
  get scrollDebounceTime(): number {
    return this._scrollDebounceTime;
  }

  @Input()
  set bufferAmount(value: number | undefined) {
    this._bufferAmount = value;
    this.scheduleRefresh();
  }
  get bufferAmount(): number {
    return typeof this._bufferAmount === 'number' && this._bufferAmount >= 0
      ? this._bufferAmount
      : this.enableUnequalChildrenSizes ? 5 : 0;
  }

  @Input()
  set enableUnequalChildrenSizes(value: boolean) {
    if (this._enableUnequalChildrenSizes === value) return;
    this._enableUnequalChildrenSizes = value;
    this.resetMeasurements();
    this.scheduleRefresh(true);
  }
  get enableUnequalChildrenSizes(): boolean {
    return this._enableUnequalChildrenSizes;
  }

  @Input()
  set items(value: unknown[] | null | undefined) {
    if (value === this._items) return;
    this._items = value ?? [];
    this.scheduleRefresh(true);
  }
  get items(): unknown[] {
    return this._items;
  }

  @Input()
  set horizontal(value: boolean) {
    this._horizontal = value;
    this.scheduleRefresh(true);
  }
  get horizontal(): boolean {
    return this._horizontal;
  }

  @Input()
  set parentScroll(value: Element | Window | undefined) {
    if (this._parentScroll === value) return;
    this.revertParentOverflow();
    this._parentScroll = value;
    this.attachScrollHandlers();
    this.updateParentOverflow();
    this.scheduleRefresh(true);
  }
  get parentScroll(): Element | Window | undefined {
    return this._parentScroll;
  }

  @Input() compareItems: (first: unknown, second: unknown) => boolean = (first, second) => first === second;

  @Output() readonly vsUpdate = new EventEmitter<unknown[]>();
  @Output() readonly vsChange = new EventEmitter<VirtualScrollerPageInfo>();
  @Output() readonly vsStart = new EventEmitter<VirtualScrollerPageInfo>();
  @Output() readonly vsEnd = new EventEmitter<VirtualScrollerPageInfo>();

  @ViewChild('content', { read: ElementRef, static: true }) private readonly contentElementRef!: ElementRef<HTMLElement>;
  @ViewChild('invisiblePadding', { read: ElementRef, static: true }) private readonly invisiblePaddingElementRef!: ElementRef<HTMLElement>;
  @ContentChild('header', { read: ElementRef }) private headerElementRef?: ElementRef<HTMLElement>;
  @ContentChild('container', { read: ElementRef }) private containerElementRef?: ElementRef<HTMLElement>;

  viewPortItems: unknown[] = [];
  get viewPortInfo(): VirtualScrollerPageInfo {
    return this.toPageInfo(this.previousViewport);
  }

  private readonly isBrowser: boolean;
  private _items: unknown[] = [];
  private _horizontal = false;
  private _parentScroll?: Element | Window;
  private _bufferAmount?: number;
  private _enableUnequalChildrenSizes = false;
  private _scrollThrottlingTime = 0;
  private _scrollDebounceTime = 0;
  private cachedItemsLength = 0;
  private previousViewport = this.emptyViewport();
  private wrapGroupDimensions: Array<WrapGroupDimension | undefined> = [];
  private minMeasuredChildWidth?: number;
  private minMeasuredChildHeight?: number;
  private calculatedScrollbarWidth = 0;
  private calculatedScrollbarHeight = 0;
  private scheduledFrame?: number;
  private pendingItemsModified = false;
  private pendingRefreshCompleted?: () => void;
  private animationFrame?: number;
  private scrollHandler?: (() => void) & { cancel?: () => void };
  private removeScrollListener?: () => void;
  private removeResizeListener?: () => void;
  private resizeObserver?: ResizeObserver;
  private contentObserver?: MutationObserver;
  private oldParentOverflow?: { x: string; y: string };
  private initialized = false;
  private zone = inject(NgZone);

  constructor(
    private readonly element: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    this.initialized = true;
    if (!this.isBrowser) {
      this.refreshInternal(true);
      return;
    }
    this.attachScrollHandlers();
    this.resizeObserver = new ResizeObserver(() => this.scheduleRefresh());
    this.resizeObserver.observe(this.getScrollElement());
    this.resizeObserver.observe(this.contentElementRef.nativeElement);
    this.contentObserver = new MutationObserver(() => this.scheduleRefresh());
    this.contentObserver.observe(this.contentElementRef.nativeElement, { childList: true, subtree: true });
    this.scheduleRefresh(true);
  }

  ngAfterContentChecked(): void {
    if (!this.initialized) return;
    const lengthChanged = this.cachedItemsLength !== this.items.length;
    this.cachedItemsLength = this.items.length;
    let visibleItemsChanged = false;
    if (!lengthChanged && this.previousViewport.startIndexWithBuffer >= 0 && this.viewPortItems.length > 0) {
      visibleItemsChanged = this.viewPortItems.some((item, index) => !this.compareItems(this.items[this.previousViewport.startIndexWithBuffer + index], item));
    }
    this.scheduleRefresh(lengthChanged || visibleItemsChanged);
  }

  ngOnDestroy(): void {
    if (this.scheduledFrame !== undefined) cancelAnimationFrame(this.scheduledFrame);
    if (this.animationFrame !== undefined) cancelAnimationFrame(this.animationFrame);
    this.removeScrollHandlers();
    this.resizeObserver?.disconnect();
    this.contentObserver?.disconnect();
    this.revertParentOverflow();
  }

  refresh(): void {
    this.scheduleRefresh(true);
  }

  invalidateAllCachedMeasurements(): void {
    this.resetMeasurements();
    this.scheduleRefresh();
  }

  invalidateCachedMeasurementForItem(item: unknown): void {
    this.invalidateCachedMeasurementAtIndex(this.items.indexOf(item));
  }

  invalidateCachedMeasurementAtIndex(index: number): void {
    if (this.enableUnequalChildrenSizes && index >= 0) {
      this.wrapGroupDimensions[Math.floor(index / this.countItemsPerWrapGroup())] = undefined;
    } else {
      this.minMeasuredChildWidth = undefined;
      this.minMeasuredChildHeight = undefined;
    }
    this.scheduleRefresh();
  }

  scrollInto(item: unknown, alignToBeginning = true, additionalOffset = 0, animationMilliseconds?: number, completed?: () => void): void {
    const index = this.items.indexOf(item);
    if (index >= 0) this.scrollToIndex(index, alignToBeginning, additionalOffset, animationMilliseconds, completed);
  }

  scrollToIndex(index: number, alignToBeginning = true, additionalOffset = 0, animationMilliseconds?: number, completed?: () => void): void {
    let retriesRemaining = 5;
    const retryIfNeeded = () => {
      retriesRemaining--;
      if (retriesRemaining <= 0) {
        completed?.();
        return;
      }
      const dimensions = this.calculateDimensions();
      const desiredStartIndex = Math.min(Math.max(index, 0), Math.max(dimensions.itemCount - 1, 0));
      if (this.previousViewport.startIndex === desiredStartIndex) {
        completed?.();
        return;
      }
      this.scrollToIndexInternal(index, alignToBeginning, additionalOffset, 0, retryIfNeeded);
    };
    this.scrollToIndexInternal(index, alignToBeginning, additionalOffset, animationMilliseconds, retryIfNeeded);
  }

  private scrollToIndexInternal(index: number, alignToBeginning: boolean, additionalOffset: number, animationMilliseconds: number | undefined, completed: () => void): void {
    const dimensions = this.calculateDimensions();
    const boundedIndex = Math.min(Math.max(index, 0), Math.max(dimensions.itemCount - 1, 0));
    let position = this.calculatePadding(boundedIndex, dimensions) + additionalOffset;
    if (!alignToBeginning) position -= dimensions.wrapGroupsPerPage * dimensions[this.childScrollDimension];
    this.scrollToPosition(position, animationMilliseconds, completed);
  }

  scrollToPosition(position: number, animationMilliseconds = this.scrollAnimationTime, completed?: () => void): void {
    if (!this.isBrowser) {
      completed?.();
      return;
    }
    const scrollElement = this.getScrollElement();
    const target = position + this.getElementsOffset();
    if (this.animationFrame !== undefined) cancelAnimationFrame(this.animationFrame);
    if (!animationMilliseconds) {
      this.renderer.setProperty(scrollElement, this.scrollProperty, target);
      this.scheduleRefresh(false, completed);
      return;
    }
    const start = scrollElement[this.scrollProperty];
    const distance = target - start;
    const startedAt = performance.now();
    const animate = (now: number) => {
      const progress = Math.min((now - startedAt) / animationMilliseconds, 1);
      this.renderer.setProperty(scrollElement, this.scrollProperty, start + distance * (1 - (1 - progress) * (1 - progress)));
      this.scheduleRefresh();
      if (progress < 1) this.animationFrame = requestAnimationFrame(animate);
      else {
        this.animationFrame = undefined;
        this.scheduleRefresh(false, completed);
      }
    };
    this.zone.runOutsideAngular(() => {
      this.animationFrame = requestAnimationFrame(animate);
    });
  }

  private scheduleRefresh(itemsModified = false, completed?: () => void): void {
    if (!this.initialized || !this.isBrowser) return;
    this.pendingItemsModified ||= itemsModified;
    if (completed) {
      const previousCompleted = this.pendingRefreshCompleted;
      this.pendingRefreshCompleted = previousCompleted
        ? () => {
            previousCompleted();
            completed();
          }
        : completed;
    }
    if (this.scheduledFrame !== undefined) return;
    this.zone.runOutsideAngular(() => {
      this.scheduledFrame = requestAnimationFrame(() => {
        this.scheduledFrame = undefined;
        const pendingItemsModified = this.pendingItemsModified;
        const pendingRefreshCompleted = this.pendingRefreshCompleted;
        this.pendingItemsModified = false;
        this.pendingRefreshCompleted = undefined;
        this.refreshInternal(pendingItemsModified, pendingRefreshCompleted);
      });
    });
  }

  private refreshInternal(itemsModified: boolean, completed?: () => void): void {
    if (itemsModified) this.resetMeasurements();
    const viewport = this.calculateViewport();
    const previous = this.previousViewport;
    const rangeChanged = itemsModified || viewport.startIndex !== previous.startIndex || viewport.endIndex !== previous.endIndex;
    const bufferedRangeChanged = itemsModified || viewport.startIndexWithBuffer !== previous.startIndexWithBuffer || viewport.endIndexWithBuffer !== previous.endIndexWithBuffer;
    const scrollLengthChanged = viewport.scrollLength !== previous.scrollLength;
    const paddingChanged = viewport.padding !== previous.padding;
    const scrollChanged = viewport.scrollStartPosition !== previous.scrollStartPosition || viewport.scrollEndPosition !== previous.scrollEndPosition;
    this.previousViewport = viewport;
    if (scrollLengthChanged) {
      this.renderer.setStyle(this.invisiblePaddingElementRef.nativeElement, this.invisiblePaddingProperty, `${viewport.scrollLength}px`);
    }
    if (paddingChanged) {
      const content = this.contentElementRef.nativeElement;
      if (this.useMarginInsteadOfTranslate) this.renderer.setStyle(content, this.marginProperty, `${viewport.padding}px`);
      else this.renderer.setStyle(content, 'transform', `${this.translateDirection}(${viewport.padding}px)`);
    }
    this.positionHeader(viewport);
    const handleChanged = () => {
      this.viewPortItems = viewport.startIndexWithBuffer >= 0 ? this.items.slice(viewport.startIndexWithBuffer, viewport.endIndexWithBuffer + 1) : [];
      this.vsUpdate.emit(this.viewPortItems);
      if (rangeChanged) {
        const pageInfo = this.toPageInfo(viewport);
        if (viewport.startIndex !== previous.startIndex || itemsModified) this.vsStart.emit(pageInfo);
        if (viewport.endIndex !== previous.endIndex || itemsModified) this.vsEnd.emit(pageInfo);
        this.vsChange.emit(pageInfo);
      }
      this.changeDetectorRef.markForCheck();
      completed?.();
    };
    if (bufferedRangeChanged || scrollChanged) {
      if (this.executeRefreshOutsideAngularZone) {
        handleChanged();
      } else {
        this.zone.run(handleChanged);
      }
    } else {
      completed?.();
    }
  }

  private calculateViewport(): Viewport {
    const dimensions = this.calculateDimensions();
    const offset = this.getElementsOffset();
    let scrollStartPosition = this.getScrollStartPosition() - offset;
    if (!(this.parentScroll instanceof Window) && scrollStartPosition > dimensions.scrollLength) scrollStartPosition = dimensions.scrollLength;
    scrollStartPosition = Math.max(scrollStartPosition, 0);
    const pageInfo = this.calculatePageInfo(scrollStartPosition, dimensions);
    return { ...pageInfo, padding: Math.round(this.calculatePadding(pageInfo.startIndexWithBuffer, dimensions)), scrollLength: Math.round(dimensions.scrollLength) };
  }

  private calculateDimensions(): Dimensions {
    const scrollElement = this.getScrollElement();
    const maximumScrollbarSize = 25;
    this.calculatedScrollbarHeight = Math.max(Math.min(scrollElement.offsetHeight - scrollElement.clientHeight, maximumScrollbarSize), this.calculatedScrollbarHeight);
    this.calculatedScrollbarWidth = Math.max(Math.min(scrollElement.offsetWidth - scrollElement.clientWidth, maximumScrollbarSize), this.calculatedScrollbarWidth);
    let viewportWidth = scrollElement.offsetWidth - (this.scrollbarWidth ?? (this.calculatedScrollbarWidth || (this.horizontal ? 0 : maximumScrollbarSize)));
    let viewportHeight = scrollElement.offsetHeight - (this.scrollbarHeight ?? (this.calculatedScrollbarHeight || (this.horizontal ? maximumScrollbarSize : 0)));
    const content = this.getContentElement();
    const itemsPerWrapGroup = this.countItemsPerWrapGroup();
    let defaultChildWidth: number;
    let defaultChildHeight: number;
    let wrapGroupsPerPage: number;
    if (!this.isBrowser) {
      viewportWidth = this.ssrViewportWidth;
      viewportHeight = this.ssrViewportHeight;
      defaultChildWidth = this.ssrChildWidth ?? viewportWidth;
      defaultChildHeight = this.ssrChildHeight ?? viewportHeight;
      wrapGroupsPerPage = this.horizontal
        ? Math.max(Math.ceil(viewportWidth / defaultChildWidth), 1)
        : Math.max(Math.ceil(viewportHeight / defaultChildHeight), 1);
    } else if (!this.enableUnequalChildrenSizes) {
      this.measureMinimumChild(content, viewportWidth, viewportHeight);
      defaultChildWidth = this.childWidth ?? this.minMeasuredChildWidth ?? viewportWidth;
      defaultChildHeight = this.childHeight ?? this.minMeasuredChildHeight ?? viewportHeight;
      wrapGroupsPerPage = this.horizontal ? Math.max(Math.ceil(viewportWidth / defaultChildWidth), 1) : Math.max(Math.ceil(viewportHeight / defaultChildHeight), 1);
    } else {
      const visibleSize = this.measureUnequalChildren(content, itemsPerWrapGroup, viewportWidth, viewportHeight);
      defaultChildWidth = this.childWidth ?? visibleSize.averageWidth ?? viewportWidth;
      defaultChildHeight = this.childHeight ?? visibleSize.averageHeight ?? viewportHeight;
      const remaining = this.horizontal ? viewportWidth - visibleSize.width : viewportHeight - visibleSize.height;
      wrapGroupsPerPage = Math.max(visibleSize.groups + Math.ceil(Math.max(remaining, 0) / (this.horizontal ? defaultChildWidth : defaultChildHeight)), 1);
    }
    const itemCount = this.items.length;
    const itemsPerPage = Math.max(itemsPerWrapGroup * wrapGroupsPerPage, 1);
    const numberOfWrapGroups = Math.ceil(itemCount / itemsPerWrapGroup);
    const defaultLength = this.horizontal ? defaultChildWidth : defaultChildHeight;
    let scrollLength = this.headerElementRef?.nativeElement.clientHeight ?? 0;
    for (let index = 0; index < numberOfWrapGroups; index++) scrollLength += this.wrapGroupDimensions[index]?.[this.childScrollDimension] ?? defaultLength;
    const viewportLength = this.horizontal ? viewportWidth : viewportHeight;
    return { childHeight: defaultChildHeight, childWidth: defaultChildWidth, itemCount, itemsPerPage, itemsPerWrapGroup, maxScrollPosition: Math.max(scrollLength - viewportLength, 0), pageCountFractional: itemCount / itemsPerPage, scrollLength, viewportLength, wrapGroupsPerPage };
  }

  private measureMinimumChild(content: HTMLElement, viewportWidth: number, viewportHeight: number): void {
    const child = content.children.item(0) as HTMLElement | null;
    if (!child) return;
    this.minMeasuredChildWidth ??= viewportWidth;
    this.minMeasuredChildHeight ??= viewportHeight;
    const size = this.getElementSize(child);
    this.minMeasuredChildWidth = Math.min(this.minMeasuredChildWidth, size.width);
    this.minMeasuredChildHeight = Math.min(this.minMeasuredChildHeight, size.height);
  }

  private measureUnequalChildren(content: HTMLElement, itemsPerWrapGroup: number, viewportWidth: number, viewportHeight: number): { averageWidth?: number; averageHeight?: number; width: number; height: number; groups: number } {
    let itemIndex = Math.max(this.previousViewport.startIndexWithBuffer, 0);
    let maxWidth = 0;
    let maxHeight = 0;
    let visibleWidth = 0;
    let visibleHeight = 0;
    let groups = 0;
    let scrollOffset = this.getScrollElement()[this.scrollProperty] - this.previousViewport.padding;
    for (const node of Array.from(content.children)) {
      const size = this.getElementSize(node as HTMLElement);
      maxWidth = Math.max(maxWidth, size.width);
      maxHeight = Math.max(maxHeight, size.height);
      itemIndex++;
      if (itemIndex % itemsPerWrapGroup === 0) {
        const groupIndex = Math.floor((itemIndex - 1) / itemsPerWrapGroup);
        this.wrapGroupDimensions[groupIndex] = { childWidth: maxWidth, childHeight: maxHeight, items: this.items.slice(itemIndex - itemsPerWrapGroup, itemIndex) };
        if (this.horizontal) {
          let visibleGroupWidth = Math.min(maxWidth, Math.max(viewportWidth - visibleWidth, 0));
          const consumedScrollOffset = Math.min(scrollOffset, visibleGroupWidth);
          visibleGroupWidth -= consumedScrollOffset;
          scrollOffset -= consumedScrollOffset;
          visibleWidth += visibleGroupWidth;
          if (visibleGroupWidth > 0 && visibleWidth <= viewportWidth) groups++;
        } else {
          let visibleGroupHeight = Math.min(maxHeight, Math.max(viewportHeight - visibleHeight, 0));
          const consumedScrollOffset = Math.min(scrollOffset, visibleGroupHeight);
          visibleGroupHeight -= consumedScrollOffset;
          scrollOffset -= consumedScrollOffset;
          visibleHeight += visibleGroupHeight;
          if (visibleGroupHeight > 0 && visibleHeight <= viewportHeight) groups++;
        }
        maxWidth = 0;
        maxHeight = 0;
      }
    }
    const known = this.wrapGroupDimensions.filter((dimension): dimension is WrapGroupDimension => Boolean(dimension));
    return { averageWidth: known.length ? known.reduce((sum, dimension) => sum + dimension.childWidth, 0) / known.length : undefined, averageHeight: known.length ? known.reduce((sum, dimension) => sum + dimension.childHeight, 0) / known.length : undefined, width: visibleWidth, height: visibleHeight, groups };
  }

  private calculatePadding(startIndexWithBuffer: number, dimensions: Dimensions): number {
    const startingGroup = Math.floor(Math.max(startIndexWithBuffer, 0) / dimensions.itemsPerWrapGroup);
    let padding = 0;
    for (let index = 0; index < startingGroup; index++) padding += this.wrapGroupDimensions[index]?.[this.childScrollDimension] ?? dimensions[this.childScrollDimension];
    return padding;
  }

  private calculatePageInfo(scrollPosition: number, dimensions: Dimensions): VirtualScrollerPageInfo {
    if (!dimensions.itemCount) return this.emptyViewport();
    let scrollPercentage = dimensions.scrollLength ? scrollPosition / dimensions.scrollLength : 0;
    if (this.enableUnequalChildrenSizes) {
      let coveredLength = 0;
      const groups = Math.ceil(dimensions.itemCount / dimensions.itemsPerWrapGroup);
      for (let index = 0; index < groups; index++) {
        coveredLength += this.wrapGroupDimensions[index]?.[this.childScrollDimension] ?? dimensions[this.childScrollDimension];
        if (scrollPosition < coveredLength) {
          scrollPercentage = index / groups;
          break;
        }
      }
    }
    const fractionalStart = Math.min(Math.max(scrollPercentage * dimensions.pageCountFractional, 0), dimensions.pageCountFractional) * dimensions.itemsPerPage;
    let startIndex = Math.min(Math.floor(fractionalStart), Math.max(dimensions.itemCount - dimensions.itemsPerPage - 1, 0));
    startIndex -= startIndex % dimensions.itemsPerWrapGroup;
    if (this.stripedTable) startIndex -= startIndex % (2 * dimensions.itemsPerWrapGroup);
    let endIndex = Math.ceil(fractionalStart) + dimensions.itemsPerPage - 1;
    const remainder = (endIndex + 1) % dimensions.itemsPerWrapGroup;
    if (remainder) endIndex += dimensions.itemsPerWrapGroup - remainder;
    startIndex = Math.min(Math.max(startIndex, 0), dimensions.itemCount - 1);
    endIndex = Math.min(Math.max(endIndex, 0), dimensions.itemCount - 1);
    const buffer = this.bufferAmount * dimensions.itemsPerWrapGroup;
    return { startIndex, endIndex, startIndexWithBuffer: Math.max(startIndex - buffer, 0), endIndexWithBuffer: Math.min(endIndex + buffer, dimensions.itemCount - 1), scrollStartPosition: scrollPosition, scrollEndPosition: scrollPosition + dimensions.viewportLength, maxScrollPosition: dimensions.maxScrollPosition };
  }

  private attachScrollHandlers(): void {
    if (!this.initialized || !this.isBrowser) return;
    this.removeScrollHandlers();
    this.scrollHandler = this.createScrollHandler();

    this.zone.runOutsideAngular(() => {
      this.removeScrollListener = this.renderer.listen(
        this.parentScroll instanceof Window ? 'window' : this.getScrollElement(),
        'scroll',
        this.scrollHandler,
      );

      if (this.parentScroll instanceof Window) {
        this.removeResizeListener = this.renderer.listen('window', 'resize', this.scrollHandler);
      }
    });
  }

  private removeScrollHandlers(): void {
    this.removeScrollListener?.();
    this.removeResizeListener?.();
    this.scrollHandler?.cancel?.();
    this.removeScrollListener = undefined;
    this.removeResizeListener = undefined;
    this.scrollHandler = undefined;
  }

  private createScrollHandler(): (() => void) & { cancel?: () => void } {
    if (this.scrollDebounceTime) return this.debounce(() => this.scheduleRefresh(), this.scrollDebounceTime);
    if (this.scrollThrottlingTime) return this.throttleTrailing(() => this.scheduleRefresh(), this.scrollThrottlingTime);
    return () => this.scheduleRefresh();
  }

  private debounce(callback: () => void, wait: number): (() => void) & { cancel?: () => void } {
    const throttled = this.throttleTrailing(callback, wait);
    const handler = () => {
      throttled.cancel?.();
      throttled();
    };
    handler.cancel = () => throttled.cancel?.();
    return handler;
  }

  private throttleTrailing(callback: () => void, wait: number): (() => void) & { cancel?: () => void } {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const handler = () => {
      if (timeout !== undefined) return;
      if (wait <= 0) callback();
      else timeout = setTimeout(() => {
        timeout = undefined;
        callback();
      }, wait);
    };
    handler.cancel = () => {
      if (timeout !== undefined) clearTimeout(timeout);
      timeout = undefined;
    };
    return handler;
  }

  private updateParentOverflow(): void {
    const scrollElement = this.getScrollElement();
    if (!this.modifyOverflowStyleOfParentScroll || scrollElement === this.element.nativeElement) return;
    this.oldParentOverflow = { x: scrollElement.style.overflowX, y: scrollElement.style.overflowY };
    scrollElement.style.overflowX = this.horizontal ? 'auto' : 'visible';
    scrollElement.style.overflowY = this.horizontal ? 'visible' : 'auto';
  }

  private revertParentOverflow(): void {
    if (!this.oldParentOverflow) return;
    const scrollElement = this.getScrollElement();
    scrollElement.style.overflowX = this.oldParentOverflow.x;
    scrollElement.style.overflowY = this.oldParentOverflow.y;
    this.oldParentOverflow = undefined;
  }

  private getScrollElement(): HTMLElement {
    if (this.parentScroll instanceof Window) return (this.document.scrollingElement || this.document.documentElement) as HTMLElement;
    return (this.parentScroll as HTMLElement | undefined) ?? this.element.nativeElement;
  }

  private getContentElement(): HTMLElement {
    return this.containerElementRef?.nativeElement ?? this.contentElementRef.nativeElement;
  }

  private countItemsPerWrapGroup(): number {
    if (!this.isBrowser) return Math.max(Math.round(this.horizontal ? this.ssrViewportHeight / (this.ssrChildHeight ?? this.ssrViewportHeight) : this.ssrViewportWidth / (this.ssrChildWidth ?? this.ssrViewportWidth)), 1);
    const children = this.getContentElement().children;
    if (!children.length) return 1;
    const property = this.horizontal ? 'offsetLeft' : 'offsetTop';
    const firstOffset = (children.item(0) as HTMLElement)[property];
    let result = 1;
    while (result < children.length && (children.item(result) as HTMLElement)[property] === firstOffset) result++;
    return result;
  }

  private getElementsOffset(): number {
    if (!this.isBrowser) return 0;
    let offset = this.containerElementRef?.nativeElement[this.offsetProperty] ?? 0;
    if (!this.parentScroll) return offset;
    const elementRect = this.getElementSize(this.element.nativeElement);
    const scrollElement = this.getScrollElement();
    const scrollRect = this.getElementSize(scrollElement);
    offset += this.horizontal ? elementRect.left - scrollRect.left : elementRect.top - scrollRect.top;
    if (!(this.parentScroll instanceof Window)) offset += scrollElement[this.scrollProperty];
    return offset;
  }

  private getScrollStartPosition(): number {
    if (!this.isBrowser) return 0;
    if (this.parentScroll instanceof Window) return this.horizontal ? this.document.defaultView?.scrollX ?? 0 : this.document.defaultView?.scrollY ?? 0;
    return this.getScrollElement()[this.scrollProperty] || 0;
  }

  private positionHeader(viewport: Viewport): void {
    if (!this.headerElementRef) return;
    const offset = Math.max(this.getScrollElement()[this.scrollProperty] - viewport.padding - this.getElementsOffset() + this.headerElementRef.nativeElement.clientHeight, 0);
    this.renderer.setStyle(this.headerElementRef.nativeElement, 'transform', `${this.translateDirection}(${offset}px)`);
  }

  private getElementSize(element: HTMLElement): DOMRect {
    const rect = element.getBoundingClientRect();
    const styles = this.document.defaultView?.getComputedStyle(element);
    const top = Number.parseInt(styles?.marginTop ?? '0', 10) || 0;
    const bottom = Number.parseInt(styles?.marginBottom ?? '0', 10) || 0;
    const left = Number.parseInt(styles?.marginLeft ?? '0', 10) || 0;
    const right = Number.parseInt(styles?.marginRight ?? '0', 10) || 0;
    return new DOMRect(rect.x + left, rect.y + top, rect.width + left + right, rect.height + top + bottom);
  }

  private resetMeasurements(): void {
    this.wrapGroupDimensions = [];
    this.minMeasuredChildWidth = undefined;
    this.minMeasuredChildHeight = undefined;
  }

  private toPageInfo(viewport: Viewport): VirtualScrollerPageInfo {
    const { padding: _padding, scrollLength: _scrollLength, ...pageInfo } = viewport;
    return pageInfo;
  }

  private emptyViewport(): Viewport {
    return { startIndex: -1, endIndex: -1, startIndexWithBuffer: -1, endIndexWithBuffer: -1, scrollStartPosition: 0, scrollEndPosition: 0, maxScrollPosition: 0, padding: 0, scrollLength: 0 };
  }

  private get childScrollDimension(): 'childHeight' | 'childWidth' { return this.horizontal ? 'childWidth' : 'childHeight'; }
  private get invisiblePaddingProperty(): 'width' | 'height' { return this.horizontal ? 'width' : 'height'; }
  private get marginProperty(): 'margin-left' | 'margin-top' { return this.horizontal ? 'margin-left' : 'margin-top'; }
  private get offsetProperty(): 'offsetLeft' | 'offsetTop' { return this.horizontal ? 'offsetLeft' : 'offsetTop'; }
  private get scrollProperty(): 'scrollLeft' | 'scrollTop' { return this.horizontal ? 'scrollLeft' : 'scrollTop'; }
  private get translateDirection(): 'translateX' | 'translateY' { return this.horizontal ? 'translateX' : 'translateY'; }
}
